<template>
    <b-modal :active.sync="modalOpen" :can-cancel="false" aria-modal :width="800" scroll="keep">
        <header class="modal-card-head">
            <p class="modal-card-title">PlanitEasy Connect</p>
        </header>
        <section class="modal-card-body">
            <div v-if="type == 'select-organisation'">
                <p>Met welke organisatie wil je proberen te verbinden?</p>
                <b-field label="Organisatie">
                    <span>
                        <b-select placeholder="Selecteer een organisatie" v-model="organisation" expanded>
                            <option
                                v-for="organisation in organisations"
                                :value="organisation.id"
                                :key="'organisatie_'+organisation.id"
                            >(ID: {{ organisation.id }}) {{ organisation.name }}</option>
                        </b-select>
                    </span>
                </b-field>
            </div>
            <div v-else class="text-center">
                <div class="lds-ripple" v-show="type != 'prompt_droplet'"><div></div><div></div></div>

                <div v-if="type == 'search'">
                    <h4>Verbinding zoeken met PlanitEasy Connect...</h4>
                    <h5 :class="{ 'text-danger': countDownTimer <= 3 }">{{ countDownTimer }}</h5>

                    <div v-show="showConnections">
                        <h3>
                            PlanitEasy Connect: <span :class="{ 'text-danger': !connections.pit, 'text-success': connections.pit }">{{ !connections.pit ? 'Geen verbinding' : 'Verbonden' }}</span><br>
                            Paxton NET2 API: <span :class="{ 'text-danger': !connections.paxton, 'text-success': connections.paxton }">{{ !connections.paxton ? 'Geen verbinding' : 'Verbonden' }}</span>
                        </h3>
                    </div>
                </div>
                <div v-if="type == 'fetch-access-levels'">
                    <h4>Autorisaties ophalen..</h4>
                </div>
                <div v-if="type == 'creating_access'">
                    <h4>Autorisatie aanmaken..</h4>
                </div>
                <div v-show="type == 'add_droplet'">
                    <h4>Plaats de tag op de tag-lezer</h4>

                    <b-field label="Tag nummer">
                        <b-input ref="dropletInput" class="text-center" v-if="_.has(record, 'keyfob')" v-model="tokenValue"></b-input>
                    </b-field>
                </div>
                <div v-if="type == 'editing_user'">
                    <h4>Gebruiker en toegang aanpassen..</h4>
                </div>

                <div v-if="type == 'prompt_droplet'">
                    <h4>Wil je een toegang aanmaken voor een tag?</h4>
                    <br>
                    <b-button type="is-light" @click="cancel">Annuleren</b-button>
                    <b-button type="is-primary" @click="addAuthorisation(false)">Zonder tag</b-button>
                    <b-button type="is-success" @click="addAuthorisation(true)">Met tag</b-button>
                </div>
            </div>
        </section>
    </b-modal>
</template>

<script>
import toastMixins from '@/mixins/toastMixins.js'
import { throws } from 'assert'

const defaultConnections = {
    pit: false,
    paxton: false
}

export default {
    name: 'ConnectionModal',

    mixins: [toastMixins],

    props: {
        organisationId: {
            type: Number,
            required: false,
            default: null
        },

        embedded_from: {
            type: String,
            required: true
        },

        editingDroplet: {
            type: Number,
            required: false,
            default: 0
        },

        record: {
            type: Object,
            required: false
        }
    },

    data() {
        return {
            modalOpen: false,
            type: 'select-organisation',
            organisation: null,
            pusher: null,

            connected: false,
            connections: {...defaultConnections},
            showConnections: false,
            countDownTimer: 31,
            dropletTokenAmount: 8, // How many characters is the droplet number?

            tokenValue: ''
        }
    },

    computed: {
        user() {
            return this.$store.state.user
        },

        organisations() {
            return this.$store.state.organisations
        }
    },

    methods: {
        toggleModal() {
            this.modalOpen = !this.modalOpen

            if(!this.modalOpen){
                this.disconnectConnection()
            }
        },

        checkConnection() {
            return new Promise((resolve, reject) => {
                this.connected = false
                this.connections = {...defaultConnections}
                this.showConnections = false

                // Giving the prop takes some time to set.

                setTimeout(() => {
                    if(_.isNull(this.organisationId) && _.isNull(this.organisation)){
                        if(this.user.role != 'pit') {
                            console.error('Geen organisatie!')
                            return resolve('no_organisation')
                        }
                    }

                    // Open modal if not open yet.
                    if(!this.modalOpen) this.modalOpen = true

                    this.countDownTimer = 31
                    this.startCountdown()

                    const organisationId = !_.isNull(this.organisationId) ? this.organisationId : this.organisation
                    if(_.isNull(this.organisationId) && _.isNull(this.organisation) && this.user.role == 'pit'){
                        this.type = 'select-organisation'
                    } else {
                        this.type = 'search'
                    }

                    try {
                        window.Pusher.subscribe('Connect');
                    } catch (error) {
                        console.error('Error subscribing to Pusher channel:', error);
                        this.showError('Kan geen verbinding maken met de PlanitEasy Connect server!')
                        this.$set(this, 'modalOpen', false)
                        this.disconnectConnection()
                        resolve('error')
                    }

                    window.Pusher.bind('Connect', (e) => {
                        if(_.has(e.message, 'organisation') && e.message.organisation == organisationId){
                            if (this.$store.state.env == 'local' || this.$store.state.env == 'test') {
                                console.log('📩', e.message)
                            }

                            if (e.message.type == 'pong_response') {
                                this.connections.pit = e.message.connection_pit
                                this.connections.paxton = e.message.connection_paxton
                                this.connected = !!e.message.connection_pit && !!e.message.connection_paxton
                                this.showConnections = true

                                if(!!this.connected){
                                    this.showMessage('Verbinding succesvol met PlanitEasy Connect!')
                                    this.$emit('connectionSuccesful')
                                    return resolve('connection_success')
                                }

                                return reject('no_connection')
                            }

                            if (e.message.type == 'access_levels') {
                                this.$emit('passAccessLevelsAndDepartments', e.message.response)
                            }

                            if (e.message.type == 'add_droplet_created') {
                                this.$emit('passPaxtonUser', e.message.response)
                            }

                            if (e.message.type == 'fetch_error_saving') {
                                console.log('fetch_error_saving', e.message.response)

                                this.showError('Paxton'+e.message.response.response.data.message)
                                this.$router.push({name: 'ConnectOverview'})
                            }

                            if (e.message.type == 'edit_user_success_saved') {
                                this.$emit('paxtonUserSaved', e.message.response)
                            }
                        }
                    });

                    // Broadcast to all PIT clients to check if there is one connected.
                    axios.post(`/api/connect/broadcast`, {
                        type: 'check_connect_connected_ping',
                        organisation: organisationId
                    }, {headers: {'Authorization': 'bearer ' + this.$store.state.authToken}}).catch((e) => {
                        console.error(e)
                        this.showError('Kan geen verbinding maken met de PlanitEasy Connect server!')

                        this.$set(this, 'modalOpen', false)
                        this.disconnectConnection()
                        resolve('error')
                    })
                }, 200)
            })
        },

        fetchAccessLevels() {
            return new Promise((resolve, reject) => {
                setTimeout(() => {
                    const organisationId = !_.isNull(this.organisationId) ? this.organisationId : this.organisation
                    this.toggleModal()

                    this.checkConnection().then((res) => {
                        if(res == 'connection_success'){
                            this.type = 'fetch-access-levels'

                            axios.post(`/api/connect/broadcast`, {
                                type: 'fetch_access_levels',
                                organisation: organisationId
                            }, {headers: {'Authorization': 'bearer ' + this.$store.state.authToken}})

                            resolve('success')
                        } else {
                            throw new Error('Connection failed!')
                        }
                    }).catch((e) => {
                        console.log(e)
                        reject(e)
                    })
                }, 200)
            })
        },

        promptDroplet() {
            if(!this.modalOpen) this.modalOpen = true

            this.checkConnection().then(() => {
                this.type = 'prompt_droplet'
            })
        },

        editUser() {
            if(!this.modalOpen) this.modalOpen = true

            this.checkConnection().then(() => {
                const organisationId = !_.isNull(this.organisationId) ? this.organisationId : this.organisation
                this.type = 'editing_user'

                // Dont pass keyfob.
                let sendData = {...this.record}
                if(_.has(sendData, 'keyfob')){
                    delete sendData.keyfob
                }

                axios.post(`/api/connect/broadcast`, {
                    type: 'edit_user',
                    organisation: organisationId,
                    record: sendData
                }, {headers: {'Authorization': 'bearer ' + this.$store.state.authToken}})
            })
        },

        addAuthorisation(addDroplet = false, index = false) {
            this.modalOpen = true // if not already
            this.type = addDroplet ? 'add_droplet' : 'creating_access'
            const organisationId = !_.isNull(this.organisationId) ? this.organisationId : this.organisation
            let record = JSON.parse(JSON.stringify(this.record))

            if(addDroplet) {
                // Create new keyfob
                this.$emit('createNewKeyfob', index)

                setTimeout(() => {
                    this.$refs['dropletInput'].focus()
                }, 50)

                return
            }

            if(this.tokenValue.length && (this.tokenValue.length == this.dropletTokenAmount)) {
                record.keyfob[this.editingDroplet].tokenValue = this.tokenValue
            }

            axios.post(`/api/connect/broadcast`, {
                type: 'add_droplet',
                organisation: organisationId,
                record
            }, {headers: {'Authorization': 'bearer ' + this.$store.state.authToken}})
        },

        cancel() {
            this.type = 'select-organisation'
            this.toggleModal()
        },

        startCountdown(){
            --this.countDownTimer

            if(this.countDownTimer >= 1 && !this.connected && this.modalOpen){
                setTimeout(() => {
                    this.startCountdown()
                }, 1000)
            } else if(!this.connected) {
                let errorMsg = !this.connections.pit ? 'Geen verbinding met PlanitEasy Connect' : 'Geen verbinding met Paxton'

                if(!this.connections.paxton && !this.connections.pit) {
                    errorMsg += ' en geen verbinding met Paxton'
                }
                errorMsg += '!'

                this.showError(errorMsg)
                this.disconnectConnection()
            }
        },

        disconnectConnection() {
            window.Pusher.unsubscribe('Connect');

            if(this.modalOpen){
                this.toggleModal()
            }
        }
    },

    watch: {
        modalOpen(newVal){
            if(newVal == true && this.embedded_from == 'ConnectOverview'){
                // setTimeout(() => {
                //     this.checkConnection()
                // }, 200)
            } else if(newVal == false) {
                if(this.user.role == 'pit'){
                    this.organisation = null
                    this.type = 'select-organisation'
                }
                this.disconnectConnection() // to be sure
            }
        },

        organisation(newVal){
            if(!_.isNull(newVal) && this.embedded_from == 'ConnectOverview') {
                setTimeout(() => { this.checkConnection() }, 200)
            }
        },

        tokenValue(newVal) {
            console.log(newVal.length + '/' + this.dropletTokenAmount)

            if(newVal.length === this.dropletTokenAmount){
                this.addAuthorisation()
            }
        }
    }
}
</script>